import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ShoppingBag, Loader2, Gift, Info, KeyRound, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchPrice, createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

let _cutSeq = 0;
const newCut = () => ({ id: `cut-${++_cutSeq}`, width_cm: 0, height_cm: 0, quantity: 1 });

export const Configurator = () => {
  const { t, lang } = useLanguage();
  const [cuts, setCuts] = useState(() => [newCut()]);
  const [transformers, setTransformers] = useState(1);
  const [breakdown, setBreakdown] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [email, setEmail] = useState("");
  const [proCode, setProCode] = useState("");
  const [proCodeActive, setProCodeActive] = useState(false);
  const [acceptCGV, setAcceptCGV] = useState(false);

  const validCuts = useMemo(
    () =>
      cuts.filter(
        (c) =>
          Number(c.width_cm) > 0 &&
          Number(c.height_cm) > 0 &&
          Number(c.quantity) > 0 &&
          Number(c.width_cm) <= 152 &&
          Number(c.height_cm) <= 320,
      ),
    [cuts],
  );

  useEffect(() => {
    if (validCuts.length === 0) {
      setBreakdown(null);
      return;
    }
    let cancelled = false;
    setLoadingPrice(true);
    fetchPrice(validCuts, proCodeActive ? proCode : null, transformers)
      .then((d) => !cancelled && setBreakdown(d))
      .catch(() => !cancelled && setBreakdown(null))
      .finally(() => !cancelled && setLoadingPrice(false));
    return () => { cancelled = true; };
  }, [validCuts, proCode, proCodeActive, transformers]);

  const updateCut = (idx, key, val) => {
    const num = val === "" ? "" : Number(val);
    setCuts((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: num } : c)));
  };
  const addCut = () => setCuts((prev) => [...prev, newCut()]);
  const removeCut = (idx) => setCuts((prev) => prev.filter((_, i) => i !== idx));

  const applyProCode = () => {
    const code = proCode.trim().toUpperCase();
    if (!code) return;
    setProCode(code);
    setProCodeActive(true);
    toast.success(t("config.proActivated"));
  };

  const handleCheckout = async () => {
    if (validCuts.length === 0) {
      toast.error(t("config.errorNoCuts"));
      return;
    }
    if (!acceptCGV) {
      toast.error(t("config.cgvRequired"));
      return;
    }
    setLoadingCheckout(true);
    try {
      const { url } = await createCheckout(validCuts, email, proCodeActive ? proCode : null, transformers);
      if (url) window.location.href = url;
      else toast.error(t("config.errorPayment"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("config.errorPayment"));
    } finally {
      setLoadingCheckout(false);
    }
  };

  const localeMap = { fr: "fr-CH", en: "en-CH", it: "it-CH", de: "de-CH" };
  const fmt = (n) => new Intl.NumberFormat(localeMap[lang] || "fr-CH", { style: "currency", currency: "CHF" }).format(n || 0);

  const unitPrice = breakdown?.unit_price_ttc ?? 450.0;

  return (
    <section
      id="configurateur"
      data-testid="configurator-section"
      className="py-20 sm:py-32 lg:py-40 bg-[#0d0c0b] relative overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] hairline" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-xs uppercase tracking-[0.32em] text-[#d89557]">{t("config.label")}</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f4ede0] mt-4 sm:mt-6 leading-tight">
            {t("config.title1")} <span className="italic copper-text">{t("config.title2")}</span>
          </h2>
          <p className="text-[#cbc1ad] mt-6 max-w-xl mx-auto text-sm">
            {(() => {
              const parts = t("config.desc").split("{ex}");
              return (
                <>
                  {parts[0]}
                  <em className="text-[#d89557]">15,4 cm</em>
                  {parts[1] ?? ""}
                </>
              );
            })()}
          </p>
          <div className="mt-6 inline-flex items-start gap-2 px-4 py-2.5 border border-[#b87333]/30 bg-[#b87333]/5 text-left max-w-xl">
            <Info className="w-4 h-4 text-[#d89557] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#cbc1ad]">
              <span className="text-[#d89557] uppercase tracking-[0.18em]">{t("config.important")}</span>{" "}
              {t("config.noteMargin")}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="space-y-5">
              {cuts.map((cut, idx) => (
                <div key={cut.id} data-testid={`cut-row-${idx}`} className="glass-card p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <NumField testid={`cut-width-${idx}`} label={t("config.width")} value={cut.width_cm} onChange={(v) => updateCut(idx, "width_cm", v)} max={152} span={4} />
                  <NumField testid={`cut-height-${idx}`} label={t("config.height")} value={cut.height_cm} onChange={(v) => updateCut(idx, "height_cm", v)} max={320} span={4} />
                  <NumField testid={`cut-qty-${idx}`} label={t("config.qty")} value={cut.quantity} onChange={(v) => updateCut(idx, "quantity", v)} min={1} integer span={3} />
                  <div className="md:col-span-1 flex justify-end">
                    {cuts.length > 1 && (
                      <button data-testid={`remove-cut-${idx}`} onClick={() => removeCut(idx)} className="text-[#7a716a] hover:text-red-400 transition p-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button data-testid="add-cut-button" onClick={addCut} className="w-full border border-dashed border-[#b87333]/40 hover:border-[#d89557] py-4 text-sm uppercase tracking-[0.22em] text-[#cbc1ad] hover:text-[#d89557] inline-flex items-center justify-center gap-3 transition">
                <Plus className="w-4 h-4" /> {t("config.addCut")}
              </button>
            </div>

            {/* Transformateur + télécommande */}
            <div className="mt-6 p-5 border border-[#2a2422] bg-[#0a0a0a]">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#d89557] mb-1">
                    {t("config.transformerTitle")}
                  </div>
                  <p className="text-xs text-[#7a716a] leading-relaxed">
                    {t("config.transformerHint")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="transformer-minus"
                    onClick={() => setTransformers((n) => Math.max(1, n - 1))}
                    disabled={transformers <= 1}
                    className="w-10 h-10 border border-[#2a2422] hover:border-[#b87333] text-[#cbc1ad] hover:text-[#d89557] disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center"
                  >
                    −
                  </button>
                  <div data-testid="transformer-count" className="w-14 text-center font-display text-2xl text-[#f4ede0]">
                    {transformers}
                  </div>
                  <button
                    data-testid="transformer-plus"
                    onClick={() => setTransformers((n) => Math.min(20, n + 1))}
                    className="w-10 h-10 border border-[#2a2422] hover:border-[#b87333] text-[#cbc1ad] hover:text-[#d89557] transition flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10 p-5 border border-[#b87333]/20 bg-[#b87333]/5 flex items-start gap-3">
              <Gift className="w-5 h-5 text-[#d89557] mt-0.5" />
              <div>
                <p className="text-sm text-[#f4ede0]">
                  <span className="text-[#d89557]">{t("config.shippingTitle")}</span> · {t("config.shippingSub")}
                </p>
                <p className="text-xs text-[#7a716a] mt-1">{t("config.discountInfo")}</p>
              </div>
            </div>

            <div className="mt-6 p-5 border border-[#2a2422] bg-[#0a0a0a]">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <KeyRound className="w-4 h-4 text-[#d89557]" />
                <span className="text-xs uppercase tracking-[0.25em] text-[#cbc1ad]">{t("config.proSpace")}</span>
                {proCodeActive && (
                  <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#d89557] flex items-center gap-1">
                    <Check className="w-3 h-3" /> {t("config.proCodeActive")}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <input data-testid="pro-code-input" type="text" placeholder={t("config.proPlaceholder")} value={proCode} onChange={(e) => { setProCode(e.target.value); setProCodeActive(false); }} className="flex-1 bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-sm text-[#f4ede0] placeholder:text-[#7a716a] uppercase tracking-[0.15em]" />
                <button data-testid="pro-code-apply" onClick={applyProCode} className="btn-ghost-copper px-5 text-xs uppercase tracking-[0.22em]">
                  {t("config.proValidate")}
                </button>
              </div>
              <p className="text-[10px] text-[#7a716a] mt-2 uppercase tracking-[0.18em]">{t("config.proHint")}</p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass-card p-8 lg:sticky lg:top-28">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl text-[#f4ede0]">{t("config.summary")}</h3>
                {loadingPrice && <Loader2 className="w-4 h-4 animate-spin text-[#d89557]" />}
              </div>

              <div className="text-xs uppercase tracking-[0.22em] text-[#7a716a] mb-4">
                {t("config.unitPrice")} · {fmt(unitPrice)}/m² <span className="text-[#d89557]">{t("config.vatIncl")}</span> {breakdown?.is_pro && <span className="text-[#d89557]">{t("config.proTag")}</span>}
              </div>

              <div className="space-y-3 mb-6 max-h-48 overflow-y-auto pr-2">
                {breakdown?.cuts_detail?.map((c, i) => (
                  <div key={`${c.width_cm}-${c.height_cm}-${c.quantity}-${i}`} className="flex justify-between text-sm">
                    <span className="text-[#cbc1ad]">
                      {c.width_cm}×{c.height_cm} cm · ×{c.quantity} <span className="text-[#7a716a]">({c.billed_m2} m²)</span>
                    </span>
                    <span className="text-[#f4ede0] font-medium">{fmt(c.line_price)}</span>
                  </div>
                )) || <p className="text-xs text-[#7a716a]">{t("config.enterDims")}</p>}
              </div>

              <div className="border-t border-[#2a2422] pt-4 space-y-2 text-sm">
                <Row label={t("config.totalArea")} value={`${breakdown?.total_m2 ?? 0} m²`} />
                <Row label={t("config.subtotalHT")} value={fmt(breakdown?.subtotal_ht)} />
                {breakdown?.discount > 0 && <Row label={t("config.discountVolume")} value={`- ${fmt(breakdown.discount)}`} copper />}
                {breakdown?.extra_transformers_cost > 0 && (
                  <Row
                    label={`${t("config.extraTransformers")} (×${(breakdown.transformers - 1)})`}
                    value={`+ ${fmt(breakdown.extra_transformers_cost)}`}
                  />
                )}
                <Row label={`${t("config.vat")} ${((breakdown?.tva_rate ?? 0.081) * 100).toFixed(1)}%`} value={fmt(breakdown?.tva)} />
                <Row label={t("config.shipping")} value={breakdown?.shipping === 0 ? t("config.shippingFree") : fmt(breakdown?.shipping)} copper />
              </div>

              <div className="border-t border-[#b87333]/30 mt-4 pt-4 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-[0.25em] text-[#cbc1ad]">{t("config.total")}</span>
                <span data-testid="total-price" className="font-display text-4xl copper-text">{fmt(breakdown?.total)}</span>
              </div>

              <input data-testid="customer-email" type="email" placeholder={t("config.emailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-6 bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-sm text-[#f4ede0] placeholder:text-[#7a716a] transition" />

              {/* CGV mandatory acceptance */}
              <label className="mt-4 flex items-start gap-3 cursor-pointer group" data-testid="cgv-checkbox-label">
                <input
                  data-testid="accept-cgv-checkbox"
                  type="checkbox"
                  checked={acceptCGV}
                  onChange={(e) => setAcceptCGV(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#b87333] cursor-pointer"
                />
                <span className="text-xs text-[#cbc1ad] leading-relaxed select-none">
                  {t("config.cgvLabel1")}{" "}
                  <Link
                    to="/cgv"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cgv-link"
                    className="text-[#d89557] underline underline-offset-2 hover:text-[#f4ede0] transition"
                  >
                    {t("config.cgvLabel2")}
                  </Link>{" "}
                  {t("config.cgvLabel3")}
                </span>
              </label>

              {/* Devis fourniture + pose */}
              <a
                href="#devis-pose"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("devis-pose")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="install-quote-link"
                className="mt-3 block p-3 border border-[#2a2422] hover:border-[#b87333]/60 bg-[#0a0a0a] transition group"
              >
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[#d89557]">🔧</span>
                  <span className="text-[#cbc1ad] group-hover:text-[#f4ede0] transition">
                    {t("config.installQuoteCta")}
                  </span>
                  <span className="ml-auto text-[#d89557] uppercase tracking-[0.22em] text-[10px]">
                    {t("config.installQuoteAction")} →
                  </span>
                </div>
              </a>

              <button data-testid="checkout-button" disabled={loadingCheckout || !breakdown || breakdown.total <= 0 || !acceptCGV} onClick={handleCheckout} className="btn-copper w-full mt-4 py-4 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed">
                {loadingCheckout ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                {t("config.checkout")}
              </button>

              <p className="text-[10px] text-center text-[#7a716a] mt-4 uppercase tracking-[0.25em]">{t("config.checkoutFooter")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SPAN_CLASS = { 3: "md:col-span-3", 4: "md:col-span-4" };
const NumField = ({ label, value, onChange, min = 0, max, integer, span = 4, testid }) => (
  <div className={SPAN_CLASS[span] || "md:col-span-4"}>
    <label className="block text-[10px] uppercase tracking-[0.25em] text-[#7a716a] mb-2">{label}</label>
    <input data-testid={testid} type="number" step={integer ? "1" : "0.1"} min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-[#f4ede0] font-display text-xl transition" />
  </div>
);
const Row = ({ label, value, copper }) => (
  <div className="flex justify-between">
    <span className="text-[#7a716a]">{label}</span>
    <span className={copper ? "text-[#d89557]" : "text-[#f4ede0]"}>{value}</span>
  </div>
);
